import { useState, useEffect } from 'react';
import axios from 'axios';
import MainLayout from '../components/MainLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Dashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [claimants, setClaimants] = useState([]);
  const [selectedClaimant, setSelectedClaimant] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [selectedClaimant]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const statsRes = await axios.get(
        `${API_URL}/dashboard/stats${selectedClaimant ? `?claimant_id=${selectedClaimant}` : ''}`,
        { headers }
      );
      setStats(statsRes.data);

      const casesRes = await axios.get(
        `${API_URL}/cases${selectedClaimant ? `?claimant_id=${selectedClaimant}` : ''}`,
        { headers }
      );
      setCases(casesRes.data.cases);

      if (user.role === 'admin') {
        const claimantsRes = await axios.get(`${API_URL}/admin/claimants`, { headers });
        setClaimants(claimantsRes.data.claimants);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout user={user} onLogout={onLogout}>
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-description">Overview of all Samanvay ODR cases</p>
      </div>

      {user.role === 'admin' && claimants.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <select
            data-testid="claimant-selector"
            className="filter-select"
            value={selectedClaimant}
            onChange={(e) => setSelectedClaimant(e.target.value)}
          >
            <option value="">All Claimants</option>
            {claimants.map((c) => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
        </div>
      )}

        {loading ? (
          <div>Loading...</div>
        ) : (
          <>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Total Cases</div>
                <div className="stat-value">{stats?.total || 0}</div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: '#4299e1' }}>
                <div className="stat-label">New Cases</div>
                <div className="stat-value">{stats?.new || 0}</div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: '#ed8936' }}>
                <div className="stat-label">Ongoing Cases</div>
                <div className="stat-value">{stats?.ongoing || 0}</div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: '#48bb78' }}>
                <div className="stat-label">Closed Cases</div>
                <div className="stat-value">{stats?.closed || 0}</div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: '#9f7aea' }}>
                <div className="stat-label">Award Passed</div>
                <div className="stat-value">{stats?.award_passed || 0}</div>
              </div>
            </div>

            {/* Oral Hearings Section */}
            <div className="card" style={{ marginBottom: '1.5rem', borderLeft: '4px solid #667eea' }}>
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>📅</span> Upcoming Oral Hearings
                </h2>
                <span className="badge badge-ongoing">{cases.filter(c => c.stage === 'Stage 7A').length} Scheduled</span>
              </div>
              {cases.filter(c => c.stage?.startsWith('Stage 7')).length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#718096' }}>
                  No oral hearings scheduled at this time.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Case Number</th>
                        <th>Claimant</th>
                        <th>Hearing Date</th>
                        <th>Hearing Time</th>
                        <th>Stage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cases.filter(c => c.stage?.startsWith('Stage 7')).sort((a,b) => new Date(a.next_hearing_date) - new Date(b.next_hearing_date)).map((c) => (
                        <tr key={c._id} style={{ background: c.stage === 'Stage 7A' ? '#ebf8ff' : 'white' }}>
                          <td style={{ fontWeight: 600 }}>{c.case_number || c.agreement_no}</td>
                          <td>{c.claimant_id?.name}</td>
                          <td style={{ color: '#2b6cb0', fontWeight: 600 }}>
                            {c.next_hearing_date ? new Date(c.next_hearing_date).toLocaleDateString() : '-'}
                          </td>
                          <td style={{ color: '#2b6cb0', fontWeight: 600 }}>
                            {c.hearing_time || '-'}
                          </td>
                          <td>
                            <span className="badge badge-ongoing">
                              {c.stage_name || c.stage}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card">
              <div className="card-header">
                <h2 className="card-title">Recent Cases</h2>
              </div>
              {cases.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">📋</div>
                  <div className="empty-state-text">No cases found</div>
                </div>
              ) : (
                <table data-testid="cases-table" className="data-table">
                  <thead>
                    <tr>
                      <th>Case Number</th>
                      <th>Agreement No</th>
                      <th>Batch Name</th>
                      <th>Stage</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cases.slice(0, 10).map((c) => (
                      <tr key={c._id}>
                        <td>{c.case_number || 'Not assigned'}</td>
                        <td>{c.agreement_no}</td>
                        <td>{c.batch_name}</td>
                        <td>{c.stage_name || c.stage}</td>
                        <td>
                          <span className={`badge badge-${c.stage === 'new' ? 'new' : c.stage.includes('Stage') ? 'ongoing' : 'closed'}`}>
                            {c.stage === 'new' ? 'New' : c.stage.includes('Stage') ? 'Ongoing' : 'Closed'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
    </MainLayout>
  );
};

export default Dashboard;
